import React, { useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { ConfigProvider } from 'antd';
import { LoginWrapper } from '../pages/Login/LoginWrapper';
import { HomePageWrapper } from '../pages/Home/HomePageWrapper';
import { Projects } from '../pages/projects';
import { Clouds } from '../pages/Clouds';
import { Sources } from '../pages/Sources';
import { MoveGroups } from '../pages/moveGroups';
import { BinaryAssets } from '../pages/binaryAssets';
import { EditProfile } from '../pages/editProfile';
import { MigrationProfilesList } from '../pages/migrationProfilesList';
import { MigrationSettings } from '../pages/MigrationSettings/migrationSettings';
import { MigrationSetup } from '../pages/migrationSetup';
import { MigrationList } from '../pages/migrationList';
import { MigrationPlanner } from '../pages/migrationPlanner';
import { MigrationDetails } from '../pages/migrationDetails';
import { MigrationPlan } from '../pages/migrationPlan';
import { BackupApplianceSetup } from '../pages/backupApplianceSetup';
import { BackupPolicies } from '../pages/backupPolicies';
import { RecoveryApplianceSetup } from '../pages/recoveryApplianceSetup';
import { RecoveryDrPolicies } from '../pages/recoveryDrPolicies';
import { ReportingMigrationProjectPlan } from '../pages/reportingMigrationProjectPlan';
import { ReportingMigrationStatus } from '../pages/reportingMigrationStatus';
import { ReportingMigrationByUser } from '../pages/reportingMigrationByUser';
import { ReportingEntitlementStatus } from '../pages/reportingEntitlementStatus';
import { ReportingEntitlementsConsumption } from '../pages/ReportingEntitlementsConsumption';
import { ReportingBackupPolicies } from '../pages/reportingBackupPolicies';
import { ReportingBackups } from '../pages/reportingBackups';
import { Register } from '../pages/register';
import { setActiveTabLabel } from '../lib/sessionStorageHelper';
import { ReportingDrPolicies } from '../pages/reportingDrPolicies';
import { ReportingDrReplications } from '../pages/reportingDrReplications';
import { AuditLog } from '../pages/auditLog';
import { ReportingMigrationsAWSTargetInstance } from '../pages/reportingMigrationsAwsTargetInstance';
import { Admin } from '../pages/admin';
import { ArchivedMigrations } from '../pages/archivedMigrations';
import { ScheduledMigrations } from '../pages/scheduledMigrations';
import { SourceDiscovery } from '../pages/sourceDiscovery';
import { SourceInspection } from '../pages/SourceInspection';
import { ViewDmProfile } from '../pages/viewDmProfile';
import { Users } from '../pages/users';
import { DiscoveryRoutes } from './discoveryRoutes';
import { useUserCustomization } from '../hooks/useUserCustomization';

import '../styles/index.scss';

export const AppTheme = {
    token: {
        //colorPrimary: '#0A76FD', // fallback
        fontFamily: 'Open Sans, sans-serif',
        colorText: '#33475B',
    },
    components: {
        Menu: {
            itemHoverBg: 'var(--nav-menu-item-hover)',
        },
        Button: {
            // Override button colors to use CSS variables
            colorPrimary: 'var(--button-primary-background)',
            colorPrimaryHover: 'var(--button-primary-background-hover)',
            colorPrimaryActive: 'var(--button-primary-background-hover)',
        },
    },
};

export const App = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize user customization for current org
    useUserCustomization();

    // Store reference to previous route path.
    // This is used to determine if we are navigating to a new route or going back/forward in history
    const prevPathRef = useRef(location.pathname);

    const removePageContainer = () => {
        const pageContainer = document.querySelector('.page-container');
        if (pageContainer) {
            pageContainer.remove();
        }
    };

    // Remove .page-container when location changes (including browser back/forward)
    useEffect(() => {
        if (prevPathRef.current === location.pathname) return;

        removePageContainer();
        prevPathRef.current = location.pathname;
    }, [location]);

    const cleanupBackboneViews = () => {
        if (window.RM) {
            // Remove Backbone event listeners when we change routes
            // Go through each key on window.RM
            // If it has a remove function call it
            // Or call undelegateEvents and stopListening
            Object.keys(window.RM).forEach((key) => {
                const view = window.RM[key];
                if (view && typeof view.remove === 'function') {
                    view.remove();
                } else if (view && typeof view.undelegateEvents === 'function') {
                    view.undelegateEvents();
                    view.stopListening();
                }
            });
        }
    };

    useEffect(() => {
        window.reactNavigate = (path, reloadPage = false, updatedMenuState = {}) => {
            // If path includes .html remove it. The router expects "/path" format
            let updatedPath = path.replace('.html', '');

            cleanupBackboneViews();
            if (updatedMenuState && Object.keys(updatedMenuState).length > 0) {
                // Update session storage with the active menu key and tab label
                if (updatedMenuState.activeMenuKey) {
                    sessionStorage.setItem('activeMenuKey', updatedMenuState.activeMenuKey);
                }
                if (updatedMenuState.activeTabLabel) {
                    // Use helper to set activeTabLabel and dispatch event
                    setActiveTabLabel(updatedMenuState.activeTabLabel);
                }
            }
            if (reloadPage) {
                // Redirect with full page reload
                window.location.href = updatedPath;
            } else {
                navigate(updatedPath);
            }
        };
    }, [navigate]);

    return (
        <ConfigProvider theme={AppTheme}>
            <Routes>
                {/* <Route path="/" element={<Login />} /> */}
                <Route path={ROUTES.login} element={<LoginWrapper />} />
                <Route path={ROUTES.home} element={<HomePageWrapper />} />
                <Route path={ROUTES.projects} element={<Projects />} />
                <Route path={ROUTES.clouds} element={<Clouds />} />
                <Route path={ROUTES.sources} element={<Sources />} />
                <Route path={ROUTES.moveGroups} element={<MoveGroups />} />
                <Route path={ROUTES.binaryAssets} element={<BinaryAssets />} />
                <Route path={ROUTES.editProfile} element={<EditProfile />} />
                <Route path={ROUTES.migrationProfilesList} element={<MigrationProfilesList />} />
                <Route path={ROUTES.migrationSettings} element={<MigrationSettings />} />
                <Route path={ROUTES.migrationSetup} element={<MigrationSetup />} />
                <Route path={ROUTES.migrationList} element={<MigrationList />} />
                <Route path={ROUTES.migrationPlanner} element={<MigrationPlanner />} />
                <Route path={ROUTES.migrationDetails} element={<MigrationDetails />} />
                <Route path={ROUTES.migrationPlan} element={<MigrationPlan />} />
                <Route path={ROUTES.backupApplianceSetup} element={<BackupApplianceSetup />} />
                <Route path={ROUTES.backupPolicies} element={<BackupPolicies />} />
                <Route path={ROUTES.recoveryApplianceSetup} element={<RecoveryApplianceSetup />} />
                <Route path={ROUTES.recoveryDrPolicies} element={<RecoveryDrPolicies />} />
                <Route path={ROUTES.reportingMigrationProjectPlan} element={<ReportingMigrationProjectPlan />} />
                <Route path={ROUTES.reportingMigrationStatus} element={<ReportingMigrationStatus />} />
                <Route path={ROUTES.reportingMigrationByUser} element={<ReportingMigrationByUser />} />
                <Route path={ROUTES.reportingEntitlementStatus} element={<ReportingEntitlementStatus />} />
                <Route path={ROUTES.reportingEntitlementsConsumption} element={<ReportingEntitlementsConsumption />} />
                <Route path={ROUTES.reportingBackupPolicies} element={<ReportingBackupPolicies />} />
                <Route path={ROUTES.reportingBackups} element={<ReportingBackups />} />
                <Route path={ROUTES.register} element={<Register />} />
                <Route path={ROUTES.reportingDrPolicies} element={<ReportingDrPolicies />} />
                <Route path={ROUTES.reportingDrReplications} element={<ReportingDrReplications />} />
                <Route path={ROUTES.auditLog} element={<AuditLog />} />
                <Route
                    path={ROUTES.reportingMigrationsAwsTargetInstance}
                    element={<ReportingMigrationsAWSTargetInstance />}
                />
                <Route path={ROUTES.admin} element={<Admin />} />
                <Route path={ROUTES.archivedMigrations} element={<ArchivedMigrations />} />
                <Route path={ROUTES.scheduledMigrations} element={<ScheduledMigrations />} />
                <Route path={ROUTES.sourceDiscovery} element={<SourceDiscovery />} />
                <Route path={ROUTES.sourceInspection} element={<SourceInspection />} />
                <Route path={ROUTES.viewDmProfile} element={<ViewDmProfile />} />
                <Route path={ROUTES.users} element={<Users />} />
                {/* Discovery routes */}
                {DiscoveryRoutes()}
            </Routes>
        </ConfigProvider>
    );
};
